import { useEffect, useRef, useState } from 'react';
import { AGENTS, type Chat, type ChatMessage } from '@/mocks/chat';
import AgentPicker from './AgentPicker';
import ModelSelect from './ModelSelect';
import HostedCreditsCTA from './HostedCreditsCTA';
import type { HostedCreditsMode } from '../hostedCredits';
import { CHAT_SHELL_CLASS, CHAT_TRANSCRIPT_CLASS, scrollTranscriptToEnd } from '../chatScroll';

const STARTERS = [
  'What skills should I use for this outcome?',
  "What's missing from this pipeline?",
  'Create a skill that fills the biggest gap.',
];

type Props = {
  chat: Chat;
  models: string[];
  loadedModels: string[];
  onSetAgent: (id: Chat['agentId']) => void;
  onSetModel: (model: string) => void;
  onSend: (content: string) => void;
  onApplyCompose?: (recommendations: NonNullable<ChatMessage['recommendations']>) => void;
  typing: boolean;
  error?: string | null;
  locked?: boolean;
  hostedCredits?: HostedCreditsMode;
};

export default function ChatPanel({ chat, models, loadedModels, onSetAgent, onSetModel, onSend, onApplyCompose, typing, error, locked, hostedCredits }: Props) {
  const [input, setInput] = useState('');
  const transcriptRef = useRef<HTMLDivElement>(null);
  const agent = AGENTS.find((a) => a.id === chat.agentId);

  useEffect(() => {
    scrollTranscriptToEnd(transcriptRef.current);
  }, [chat.messages.length, typing]);

  const submit = () => {
    if (locked || hostedCredits) return;
    const v = input.trim();
    if (!v) return;
    onSend(v);
    setInput('');
  };

  return (
    <div className={CHAT_SHELL_CLASS}>
      <div className="h-12 shrink-0 border-b border-border flex items-center gap-2 px-4">
        <AgentPicker value={chat.agentId} onChange={onSetAgent} />
        <ModelSelect
          chat={chat}
          models={models}
          loadedModels={loadedModels}
          onSetModel={onSetModel}
          className="min-w-0 max-w-64 flex-1 bg-surface/60 border border-border rounded-md px-2 py-1.5 text-xs text-textp outline-none focus:border-accent font-mono"
        />
        <div className="ml-auto text-[11px] text-texts whitespace-nowrap">
          {chat.messages.length} message{chat.messages.length === 1 ? '' : 's'}
        </div>
      </div>

      <div ref={transcriptRef} data-testid="chat-transcript" className={CHAT_TRANSCRIPT_CLASS}>
        {chat.messages.length === 0 && !typing && (
          <div className="h-full flex flex-col items-center justify-center text-center text-texts px-6">
            <i className="ri-chat-3-line text-3xl mb-3" />
            <div className="text-sm text-textp font-medium">Design this outcome with {agent?.name ?? 'the agent'}</div>
            <div className="text-xs mt-1 max-w-sm">
              Describe the outcome. I will fill in the rest and propose a pipeline.
            </div>
            {hostedCredits ? (
              <div className="mt-4 w-full max-w-md text-left">
                <HostedCreditsCTA mode={hostedCredits} />
              </div>
            ) : (
              <div className="mt-4 flex flex-col gap-2 w-full max-w-md">
                {STARTERS.map((s) => (
                  <button
                    key={s}
                    onClick={() => onSend(s)}
                    className="text-left text-xs bg-surface hover:bg-surface2 border border-border rounded-md px-3 py-2 text-textp"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {chat.messages.map((m) => (
          <Message key={m.id} msg={m} agent={agent} onApplyCompose={locked ? undefined : onApplyCompose} />
        ))}
        {typing && <TypingIndicator agent={agent} />}
        {hostedCredits && chat.messages.length > 0 && (
          <HostedCreditsCTA mode={hostedCredits} />
        )}
        {error && !hostedCredits && (
          <div data-testid="chat-error" className="text-xs text-error bg-error/10 border border-error/30 rounded-md px-3 py-2">{error}</div>
        )}
      </div>

      <div className="shrink-0 border-t border-border p-3">
        <div className="flex items-end gap-2">
          <textarea
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            rows={2}
            placeholder={hostedCredits === 'login' ? 'Sign in to send a hosted message' : hostedCredits ? 'Buy credits to send a hosted message' : `Message ${agent?.name ?? 'agent'}...`}
            disabled={!!hostedCredits || locked}
            className="flex-1 bg-surface border border-border rounded-md px-3 py-2 text-sm text-textp placeholder:text-texts outline-none focus:border-accent resize-none disabled:opacity-50"
          />
          <button
            data-testid="chat-send"
            onClick={submit}
            disabled={!input.trim() || typing || locked || !!hostedCredits}
            className="whitespace-nowrap flex items-center justify-center w-9 h-9 rounded-md bg-accent hover:bg-accent-hover disabled:opacity-40 text-accent-text shrink-0"
            title="Send"
          >
            <i className="ri-send-plane-2-line" />
          </button>
        </div>
      </div>
    </div>
  );
}

function Message({
  msg,
  agent,
  onApplyCompose,
}: {
  msg: ChatMessage;
  agent?: { name: string; icon: string; color: string };
  onApplyCompose?: (recommendations: NonNullable<ChatMessage['recommendations']>) => void;
}) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[78%] ${isUser ? '' : 'flex gap-2.5'}`}>
        {!isUser && (
          <div
            className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
            style={{ background: `${agent?.color ?? 'var(--texts)'}22`, color: agent?.color ?? 'var(--texts)' }}
          >
            <i className={`${agent?.icon ?? 'ri-robot-2-line'} text-sm`} />
          </div>
        )}
        <div className="min-w-0">
          <div
            className={`rounded-lg px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
              isUser ? 'bg-accent text-accent-text rounded-br-sm' : 'bg-surface border border-border text-textp rounded-bl-sm'
            }`}
          >
            {msg.content}
          </div>
          {!isUser && msg.recommendations && msg.recommendations.length > 0 && (
            <div data-testid="compose-mockup" className="mt-2 rounded-lg border border-border bg-surface px-3 py-2.5">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-[11px] font-medium text-texts uppercase tracking-wide">Compose mockup</div>
                {onApplyCompose && (
                  <button
                    type="button"
                    data-testid="compose-add-to-canvas"
                    onClick={() => onApplyCompose(msg.recommendations!)}
                    className="whitespace-nowrap flex items-center gap-1 text-[11px] font-medium text-accent hover:text-accent-hover"
                  >
                    <i className="ri-add-circle-line" />
                    Add to canvas
                  </button>
                )}
              </div>
              <ol className="space-y-2">
                {msg.recommendations.map((r, i) => (
                  <li key={r.id} data-testid="compose-row" className="text-sm">
                    <div className="flex gap-2">
                      <span className="text-texts shrink-0">{i + 1}.</span>
                      <div>
                        <div data-testid="compose-name" className="font-medium text-textp">
                          {r.name}
                        </div>
                        <div data-testid="compose-why" className="text-xs text-texts mt-0.5">
                          {r.why}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}
          <div className={`text-[10px] text-texts mt-1 ${isUser ? 'text-right' : ''}`}>{msg.time}</div>
        </div>
      </div>
    </div>
  );
}

function TypingIndicator({ agent }: { agent?: { name: string; icon: string; color: string } }) {
  return (
    <div className="flex gap-2.5">
      <div
        className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
        style={{ background: `${agent?.color ?? 'var(--texts)'}22`, color: agent?.color ?? 'var(--texts)' }}
      >
        <i className={`${agent?.icon ?? 'ri-robot-2-line'} text-sm`} />
      </div>
      <div className="bg-surface border border-border rounded-lg rounded-bl-sm px-3.5 py-3 flex items-center gap-1.5">
        <span className="typing-dot" />
        <span className="typing-dot" />
        <span className="typing-dot" />
      </div>
    </div>
  );
}
